import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Code, ConnectError } from '@connectrpc/connect';
import { Skeleton, SpecraBadge, SpecraButton, SpecraFormField, SpecraInput, SpecraSelect, SpecraTextarea } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../contexts/authContext';
import { useEntityArtifacts } from '../Capture/documentHooks';
import { useRfiQuestionMutations, useRfiQuestions } from './rfiQuestionHooks';
import { useRfiSections } from './rfiSectionHooks';
export const QUESTION_TYPES = [
    {
        value: 'government_question',
        label: 'Government question'
    },
    {
        value: 'submission_instruction',
        label: 'Submission instruction'
    },
    {
        value: 'requested_attachment',
        label: 'Requested attachment'
    },
    {
        value: 'page_limit',
        label: 'Page limit'
    },
    {
        value: 'word_limit',
        label: 'Word limit'
    },
    {
        value: 'format_limit',
        label: 'Format limit'
    }
];
const OPTIONALITY = [
    {
        value: 'unknown',
        label: 'Optionality unknown'
    },
    {
        value: 'required',
        label: 'Required'
    },
    {
        value: 'optional',
        label: 'Optional'
    }
];
const ANSWERS = [
    {
        value: 'unanswered',
        label: 'Unanswered'
    },
    {
        value: 'in_progress',
        label: 'Answer in progress'
    },
    {
        value: 'answered',
        label: 'Answered'
    }
];
const label = (options, value)=>options.find((o)=>o.value === value)?.label ?? value;
const emptyDraft = {
    originalText: '',
    documentReference: '',
    artifactId: '',
    page: '',
    section: '',
    paragraph: '',
    interpretation: '',
    itemType: 'government_question',
    optionality: 'unknown'
};
function SuggestionFields({ draft, setDraft }) {
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(SpecraFormField, {
                label: "Suggested interpretation",
                children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                    "data-testid": Testids.rfiQuestions.interpretation,
                    value: draft.interpretation,
                    onChange: (e)=>setDraft({
                            interpretation: e.target.value
                        }),
                    required: true
                })
            }),
            /*#__PURE__*/ _jsx(SpecraFormField, {
                label: "Item type",
                children: /*#__PURE__*/ _jsx(SpecraSelect, {
                    "data-testid": Testids.rfiQuestions.itemType,
                    value: draft.itemType,
                    onChange: (e)=>setDraft({
                            itemType: e.target.value
                        }),
                    children: QUESTION_TYPES.map((o)=>/*#__PURE__*/ _jsx("option", {
                            value: o.value,
                            children: o.label
                        }, o.value))
                })
            }),
            /*#__PURE__*/ _jsx(SpecraFormField, {
                label: "Optionality",
                children: /*#__PURE__*/ _jsx(SpecraSelect, {
                    "data-testid": Testids.rfiQuestions.optionality,
                    value: draft.optionality,
                    onChange: (e)=>setDraft({
                            optionality: e.target.value
                        }),
                    children: OPTIONALITY.map((o)=>/*#__PURE__*/ _jsx("option", {
                            value: o.value,
                            children: o.label
                        }, o.value))
                })
            })
        ]
    });
}
function HistorySnapshot({ serialized, sections }) {
    try {
        const value = JSON.parse(serialized);
        if (!value) return /*#__PURE__*/ _jsx("p", {
            children: "Original entry"
        });
        const ids = value.SectionIDs ?? value.section_ids ?? null;
        return /*#__PURE__*/ _jsxs("dl", {
            children: [
                /*#__PURE__*/ _jsx("dt", {
                    children: "Suggested interpretation"
                }),
                /*#__PURE__*/ _jsx("dd", {
                    children: value.Interpretation ?? value.interpretation ?? 'Not recorded'
                }),
                /*#__PURE__*/ _jsx("dt", {
                    children: "Item type"
                }),
                /*#__PURE__*/ _jsx("dd", {
                    children: label(QUESTION_TYPES, value.ItemType ?? value.item_type ?? '') || 'Not recorded'
                }),
                /*#__PURE__*/ _jsx("dt", {
                    children: "Optionality"
                }),
                /*#__PURE__*/ _jsx("dd", {
                    children: label(OPTIONALITY, value.Optionality ?? value.optionality ?? 'unknown')
                }),
                /*#__PURE__*/ _jsx("dt", {
                    children: "Answer state"
                }),
                /*#__PURE__*/ _jsx("dd", {
                    children: label(ANSWERS, value.AnswerState ?? value.answer_state ?? 'unanswered')
                }),
                /*#__PURE__*/ _jsx("dt", {
                    children: "Outline sections"
                }),
                /*#__PURE__*/ _jsx("dd", {
                    children: ids === null ? 'Not recorded' : ids.length ? ids.map((id)=>sections.find((s)=>s.id === id)?.title ?? 'Section title unavailable').join(', ') : 'Not mapped'
                })
            ]
        });
    } catch  {
        return /*#__PURE__*/ _jsx("p", {
            children: "History detail unavailable"
        });
    }
}
function QuestionRow({ question: q, projectId, events }) {
    const { review, mapping } = useRfiQuestionMutations(projectId);
    const mappingBusy = useRef(false);
    const sections = useRfiSections(projectId);
    const cache = useQueryClient();
    const draftKey = [
        'rfi-question-draft',
        projectId,
        q.id
    ];
    const [editing, setEditing] = useState(false);
    const persisted = {
        interpretation: q.suggestion?.interpretation ?? '',
        itemType: q.suggestion?.itemType ?? 'government_question',
        optionality: q.suggestion?.optionality ?? 'unknown',
        answerState: q.answerState,
        sectionIds: q.sectionIds
    };
    const currentBase = JSON.stringify(persisted);
    const [draftState, setDraftState] = useState(()=>cache.getQueryData(draftKey) ?? {
            values: persisted,
            base: currentBase
        });
    const draft = draftState.values;
    const conflict = draftState.base !== currentBase;
    const patch = (p)=>{
        const next = {
            ...draftState,
            values: {
                ...draft,
                ...p
            }
        };
        setDraftState(next);
        cache.setQueryData(draftKey, next);
    };
    const acceptLatest = (keepEdits)=>{
        const next = {
            values: keepEdits ? draft : persisted,
            base: currentBase
        };
        setDraftState(next);
        cache.setQueryData(draftKey, next);
    };
    const source = q.source;
    return /*#__PURE__*/ _jsxs("article", {
        className: "rounded-md border p-4 space-y-3",
        "data-testid": Testids.rfiQuestions.row,
        id: `rfi-question-${q.id}`,
        tabIndex: -1,
        "data-question-id": q.id,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-wrap gap-2",
                children: [
                    /*#__PURE__*/ _jsx(SpecraBadge, {
                        variant: "neutral",
                        children: label(QUESTION_TYPES, q.suggestion?.itemType ?? '')
                    }),
                    /*#__PURE__*/ _jsx(SpecraBadge, {
                        variant: q.suggestion?.optionality === 'unknown' ? 'warning' : 'neutral',
                        children: label(OPTIONALITY, q.suggestion?.optionality ?? 'unknown')
                    }),
                    /*#__PURE__*/ _jsx(SpecraBadge, {
                        variant: q.answerState === 'answered' ? 'success' : 'neutral',
                        children: label(ANSWERS, q.answerState)
                    }),
                    /*#__PURE__*/ _jsx(SpecraBadge, {
                        variant: q.reviewed ? 'success' : 'warning',
                        children: q.reviewed ? 'Reviewed' : 'Needs review'
                    })
                ]
            }),
            q.stale && /*#__PURE__*/ _jsx("output", {
                className: "block text-sm",
                children: "Source stale — compare with the current document and add any omitted items manually. The original quotation is retained."
            }),
            /*#__PURE__*/ _jsxs("div", {
                children: [
                    /*#__PURE__*/ _jsx("h3", {
                        className: "font-medium",
                        children: "Exact source wording"
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        className: "whitespace-pre-wrap",
                        children: source?.originalText
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("dl", {
                className: "text-sm space-y-1",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        children: [
                            /*#__PURE__*/ _jsx("dt", {
                                className: "font-medium",
                                children: "Document reference"
                            }),
                            /*#__PURE__*/ _jsx("dd", {
                                children: source?.documentReference || 'Not provided'
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        children: [
                            /*#__PURE__*/ _jsx("dt", {
                                className: "font-medium",
                                children: "Document version"
                            }),
                            /*#__PURE__*/ _jsx("dd", {
                                children: source?.artifactId ? `${source.artifactId} · version ${source.artifactVersion}` : 'Manual reference; version unavailable'
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        children: [
                            /*#__PURE__*/ _jsx("dt", {
                                className: "font-medium",
                                children: "Page"
                            }),
                            /*#__PURE__*/ _jsx("dd", {
                                children: source?.page || 'Unknown'
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        children: [
                            /*#__PURE__*/ _jsx("dt", {
                                className: "font-medium",
                                children: "Section"
                            }),
                            /*#__PURE__*/ _jsx("dd", {
                                children: source?.section || 'Unknown'
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        children: [
                            /*#__PURE__*/ _jsx("dt", {
                                className: "font-medium",
                                children: "Paragraph"
                            }),
                            /*#__PURE__*/ _jsx("dd", {
                                children: source?.paragraph || 'Unknown'
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                children: [
                    /*#__PURE__*/ _jsx("h3", {
                        className: "font-medium",
                        children: "Suggested interpretation"
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        children: q.suggestion?.interpretation
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "space-y-2",
                children: [
                    /*#__PURE__*/ _jsxs("p", {
                        className: "text-sm",
                        children: [
                            q.sectionIds.length ? `Mapped to ${q.sectionIds.length} section(s)` : 'Not mapped',
                            " · Mapping does not mark an item answered."
                        ]
                    }),
                    [
                        'page_limit',
                        'word_limit',
                        'format_limit'
                    ].includes(q.suggestion?.itemType ?? '') ? /*#__PURE__*/ _jsx("p", {
                        children: "Package constraint — applies to the whole response, separately from section answers."
                    }) : /*#__PURE__*/ _jsxs("fieldset", {
                        disabled: mapping.isPending || review.isPending,
                        className: "space-y-2",
                        children: [
                            /*#__PURE__*/ _jsx("legend", {
                                children: "Map to outline sections"
                            }),
                            sections.isLoading ? /*#__PURE__*/ _jsx("output", {
                                children: "Loading outline sections…"
                            }) : sections.isError ? /*#__PURE__*/ _jsxs("p", {
                                role: "alert",
                                children: [
                                    "Couldn't load outline sections.",
                                    ' ',
                                    /*#__PURE__*/ _jsx(SpecraButton, {
                                        variant: "link",
                                        size: "inline",
                                        type: "button",
                                        onClick: ()=>sections.refetch(),
                                        children: "Retry sections"
                                    })
                                ]
                            }) : sections.data?.length ? sections.data.map((s)=>/*#__PURE__*/ _jsx(SpecraButton, {
                                    type: "button",
                                    variant: "outline",
                                    "data-testid": Testids.rfiQuestions.mappingToggle,
                                    "aria-pressed": q.sectionIds.includes(s.id),
                                    disabled: mapping.isPending || review.isPending,
                                    onClick: ()=>{
                                        if (mappingBusy.current) return;
                                        mappingBusy.current = true;
                                        mapping.mutate({
                                            questionId: q.id,
                                            sectionId: s.id,
                                            mapped: !q.sectionIds.includes(s.id)
                                        }, {
                                            onSettled: ()=>{
                                                mappingBusy.current = false;
                                            }
                                        });
                                    },
                                    children: s.title
                                }, s.id)) : /*#__PURE__*/ _jsx("p", {
                                children: "No outline sections yet. Add a section in the outline to map this item."
                            })
                        ]
                    }),
                    mapping.isPending && /*#__PURE__*/ _jsx("output", {
                        children: "Saving section mapping…"
                    }),
                    mapping.isError && /*#__PURE__*/ _jsx("p", {
                        role: "alert",
                        children: "Couldn't save mapping. Saved links are retained. Try the section again."
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "flex flex-wrap gap-2",
                        children: q.sectionIds.map((id)=>/*#__PURE__*/ _jsx("a", {
                                "data-testid": Testids.rfiQuestions.mappedSection,
                                href: `#rfi-section-${id}`,
                                children: sections.data?.find((s)=>s.id === id)?.title || 'Section title unavailable'
                            }, id))
                    })
                ]
            }),
            editing ? /*#__PURE__*/ _jsxs("form", {
                className: "space-y-3",
                onSubmit: (e)=>{
                    e.preventDefault();
                    if (review.isPending || conflict) return;
                    review.mutate({
                        questionId: q.id,
                        suggestion: draft,
                        answerState: draft.answerState,
                        sectionIds: q.sectionIds
                    }, {
                        onSuccess: ()=>{
                            setEditing(false);
                            cache.removeQueries({
                                queryKey: draftKey
                            });
                        }
                    });
                },
                children: [
                    /*#__PURE__*/ _jsx("p", {
                        className: "text-sm",
                        children: "Review changes the interpretation and classification. Exact source wording and history are retained."
                    }),
                    conflict && /*#__PURE__*/ _jsxs("div", {
                        role: "alert",
                        children: [
                            /*#__PURE__*/ _jsx("p", {
                                children: "This item changed since your draft began. Your edits are retained. Compare the current item above before saving."
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                type: "button",
                                variant: "outline",
                                onClick: ()=>acceptLatest(false),
                                children: "Use latest saved values"
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                type: "button",
                                variant: "outline",
                                onClick: ()=>acceptLatest(true),
                                children: "Keep my edits over latest values"
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(SuggestionFields, {
                        draft: draft,
                        setDraft: patch
                    }),
                    /*#__PURE__*/ _jsx(SpecraFormField, {
                        label: "Answer state",
                        children: /*#__PURE__*/ _jsx(SpecraSelect, {
                            "data-testid": Testids.rfiQuestions.answerState,
                            value: draft.answerState,
                            onChange: (e)=>patch({
                                    answerState: e.target.value
                                }),
                            children: ANSWERS.map((o)=>/*#__PURE__*/ _jsx("option", {
                                    value: o.value,
                                    children: o.label
                                }, o.value))
                        })
                    }),
                    review.isError && /*#__PURE__*/ _jsx("p", {
                        role: "alert",
                        children: "Couldn't save review. Your edits are retained; try again."
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        type: "submit",
                        "data-testid": Testids.rfiQuestions.save,
                        disabled: review.isPending || mapping.isPending || conflict || !draft.interpretation.trim(),
                        children: review.isPending ? 'Saving…' : 'Save review'
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        type: "button",
                        variant: "ghost",
                        disabled: review.isPending,
                        onClick: ()=>setEditing(false),
                        children: "Close review"
                    })
                ]
            }) : /*#__PURE__*/ _jsx(SpecraButton, {
                "data-testid": Testids.rfiQuestions.review,
                variant: "outline",
                onClick: ()=>{
                    setDraftState(cache.getQueryData(draftKey) ?? {
                        values: persisted,
                        base: currentBase
                    });
                    setEditing(true);
                },
                children: "Review item"
            }),
            /*#__PURE__*/ _jsxs("details", {
                children: [
                    /*#__PURE__*/ _jsxs("summary", {
                        children: [
                            "Review and mapping history (",
                            events.length,
                            ")"
                        ]
                    }),
                    events.map((event)=>/*#__PURE__*/ _jsxs("div", {
                            className: "text-sm border-t py-2",
                            children: [
                                /*#__PURE__*/ _jsxs("p", {
                                    children: [
                                        event.actor,
                                        " · ",
                                        event.createdAt?.toDate().toLocaleString()
                                    ]
                                }),
                                /*#__PURE__*/ _jsx("h4", {
                                    children: "Before"
                                }),
                                /*#__PURE__*/ _jsx(HistorySnapshot, {
                                    serialized: event.beforeJson,
                                    sections: sections.data ?? []
                                }),
                                /*#__PURE__*/ _jsx("h4", {
                                    children: "After"
                                }),
                                /*#__PURE__*/ _jsx(HistorySnapshot, {
                                    serialized: event.afterJson,
                                    sections: sections.data ?? []
                                })
                            ]
                        }, event.id))
                ]
            })
        ]
    });
}
export function RfiQuestionsPanel({ rfiResponseProjectId: projectId }) {
    const query = useRfiQuestions(projectId);
    const artifacts = useEntityArtifacts('rfi_response_project', projectId);
    const { create, extract } = useRfiQuestionMutations(projectId);
    const cache = useQueryClient();
    const draftKey = [
        'rfi-manual-question-draft',
        projectId
    ];
    const { user } = useAuth();
    const storageKey = `rfi-question-draft:${user?.id ?? ''}:${projectId}`;
    const storedDraft = ()=>{
        try {
            return JSON.parse(sessionStorage.getItem(storageKey) || 'null');
        } catch  {
            return null;
        }
    };
    const [draft, setDraft] = useState(()=>cache.getQueryData(draftKey) ?? storedDraft() ?? emptyDraft);
    const [adding, setAdding] = useState(()=>Boolean(cache.getQueryData(draftKey) ?? storedDraft()));
    const [extractArtifact, setExtractArtifact] = useState('');
    useEffect(()=>{
        if (adding) {
            cache.setQueryData([
                'rfi-manual-question-draft',
                projectId
            ], draft);
            try {
                sessionStorage.setItem(storageKey, JSON.stringify(draft));
            } catch  {
            /* A disabled browser store still allows entry and save. */ }
        }
    }, [
        draft,
        adding,
        cache,
        projectId,
        storageKey
    ]);
    const patch = (p)=>setDraft((d)=>({
                ...d,
                ...p
            }));
    if (query.error instanceof ConnectError && [
        Code.PermissionDenied,
        Code.NotFound,
        Code.Unauthenticated
    ].includes(query.error.code)) {
        return /*#__PURE__*/ _jsx("p", {
            role: "alert",
            children: "Questions unavailable. Check your access or return to your RFI Responses."
        });
    }
    const options = [
        {
            value: '',
            label: 'Manual document reference'
        },
        ...(artifacts.data ?? []).map((a)=>({
                value: a.artifactId,
                label: a.filename
            }))
    ];
    return /*#__PURE__*/ _jsxs("section", {
        className: "max-w-3xl space-y-4",
        id: "rfi-questions",
        "data-testid": Testids.rfiQuestions.panel,
        children: [
            /*#__PURE__*/ _jsx("h2", {
                className: "text-lg font-medium",
                children: "Questions and instructions"
            }),
            /*#__PURE__*/ _jsx("p", {
                className: "text-sm text-muted-foreground",
                children: "Keep the exact source wording separate from your suggested interpretation. Review extracted suggestions before relying on them."
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-wrap gap-2",
                children: [
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        "data-testid": Testids.rfiQuestions.addManual,
                        onClick: ()=>setAdding(true),
                        children: "Add manually"
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        variant: "outline",
                        disabled: query.isFetching,
                        onClick: ()=>query.refetch(),
                        children: "Refresh questions"
                    })
                ]
            }),
            query.isFetching && /*#__PURE__*/ _jsxs("output", {
                className: "block",
                "data-testid": Testids.rfiQuestions.loading,
                children: [
                    /*#__PURE__*/ _jsx("p", {
                        children: "Loading questions and extraction history…"
                    }),
                    /*#__PURE__*/ _jsx(Skeleton, {
                        className: "h-24"
                    })
                ]
            }),
            query.isError && /*#__PURE__*/ _jsx("p", {
                role: "alert",
                children: "Couldn't load questions. Refresh to retry, or add an omitted item manually."
            }),
            artifacts.isError && /*#__PURE__*/ _jsx("output", {
                className: "block text-sm",
                children: "Documents unavailable. Enter a manual document reference below."
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "space-y-2",
                children: [
                    /*#__PURE__*/ _jsx(SpecraFormField, {
                        label: "Document to extract",
                        children: /*#__PURE__*/ _jsx(SpecraSelect, {
                            "data-testid": Testids.rfiQuestions.extractArtifact,
                            value: extractArtifact,
                            onChange: (e)=>setExtractArtifact(e.target.value),
                            children: [
                                {
                                    value: '',
                                    label: 'Choose a linked document'
                                },
                                ...options.slice(1)
                            ].map((o)=>/*#__PURE__*/ _jsx("option", {
                                    value: o.value,
                                    children: o.label
                                }, o.value))
                        })
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        variant: "outline",
                        "data-testid": Testids.rfiQuestions.extract,
                        disabled: !extractArtifact || extract.isPending,
                        onClick: ()=>extract.mutate(extractArtifact),
                        children: extract.isPending ? 'Extracting questions…' : 'Extract questions'
                    }),
                    extract.isPending && /*#__PURE__*/ _jsx("output", {
                        className: "block text-sm",
                        children: "Extracting questions… You can add omitted items manually while this runs."
                    }),
                    extract.isError && /*#__PURE__*/ _jsx("p", {
                        role: "alert",
                        children: "Extraction couldn't finish. Retry extraction or add items manually."
                    }),
                    query.data?.extractions.map((x)=>/*#__PURE__*/ _jsxs("output", {
                            className: "block text-sm",
                            "data-testid": Testids.rfiQuestions.extractionState,
                            children: [
                                "Extraction ",
                                x.state,
                                x.stale ? ' · Source stale' : '',
                                " · ",
                                x.createdAt?.toDate().toLocaleString(),
                                ".",
                                ' ',
                                x.state === 'complete' ? 'Review the suggested items; add anything omitted.' : 'Review available items, retry extraction or add items manually.'
                            ]
                        }, x.id))
                ]
            }),
            adding && /*#__PURE__*/ _jsxs("form", {
                className: "rounded-md border p-4 space-y-3",
                onSubmit: (e)=>{
                    e.preventDefault();
                    if (create.isPending) return;
                    const artifact = artifacts.data?.find((a)=>a.artifactId === draft.artifactId);
                    if (draft.artifactId && !artifact) return;
                    create.mutate({
                        source: {
                            originalText: draft.originalText,
                            documentReference: draft.documentReference,
                            artifactId: artifact?.artifactId ?? '',
                            artifactVersion: artifact?.version ?? 0,
                            sha256: artifact?.checksumSha256 ?? '',
                            page: Number(draft.page) || 0,
                            section: draft.section,
                            paragraph: draft.paragraph
                        },
                        suggestion: draft
                    }, {
                        onSuccess: ()=>{
                            setAdding(false);
                            setDraft(emptyDraft);
                            try {
                                sessionStorage.removeItem(storageKey);
                            } catch  {
                            /* Browser storage may be disabled. */ }
                            cache.removeQueries({
                                queryKey: draftKey
                            });
                        }
                    });
                },
                children: [
                    /*#__PURE__*/ _jsx("h3", {
                        className: "font-medium",
                        children: "Add omitted item"
                    }),
                    /*#__PURE__*/ _jsx(SpecraFormField, {
                        label: "Exact source wording",
                        children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                            required: true,
                            "data-testid": Testids.rfiQuestions.originalWording,
                            value: draft.originalText,
                            onChange: (e)=>patch({
                                    originalText: e.target.value
                                })
                        })
                    }),
                    /*#__PURE__*/ _jsx(SpecraFormField, {
                        label: "Linked document (optional)",
                        children: /*#__PURE__*/ _jsx(SpecraSelect, {
                            "data-testid": Testids.rfiQuestions.sourceArtifact,
                            value: draft.artifactId,
                            onChange: (e)=>patch({
                                    artifactId: e.target.value
                                }),
                            children: options.map((o)=>/*#__PURE__*/ _jsx("option", {
                                    value: o.value,
                                    children: o.label
                                }, o.value))
                        })
                    }),
                    /*#__PURE__*/ _jsx(SpecraFormField, {
                        label: "Document reference",
                        children: /*#__PURE__*/ _jsx(SpecraInput, {
                            "data-testid": Testids.rfiQuestions.documentReference,
                            value: draft.documentReference,
                            onChange: (e)=>patch({
                                    documentReference: e.target.value
                                })
                        })
                    }),
                    /*#__PURE__*/ _jsx(SpecraFormField, {
                        label: "Page (if known)",
                        children: /*#__PURE__*/ _jsx(SpecraInput, {
                            type: "number",
                            min: "1",
                            "data-testid": Testids.rfiQuestions.sourcePage,
                            value: draft.page,
                            onChange: (e)=>patch({
                                    page: e.target.value
                                })
                        })
                    }),
                    /*#__PURE__*/ _jsx(SpecraFormField, {
                        label: "Section / source location",
                        children: /*#__PURE__*/ _jsx(SpecraInput, {
                            "data-testid": Testids.rfiQuestions.sourceLocation,
                            value: draft.section,
                            onChange: (e)=>patch({
                                    section: e.target.value
                                })
                        })
                    }),
                    /*#__PURE__*/ _jsx(SpecraFormField, {
                        label: "Paragraph (if known)",
                        children: /*#__PURE__*/ _jsx(SpecraInput, {
                            "data-testid": Testids.rfiQuestions.sourceParagraph,
                            value: draft.paragraph,
                            onChange: (e)=>patch({
                                    paragraph: e.target.value
                                })
                        })
                    }),
                    /*#__PURE__*/ _jsx(SuggestionFields, {
                        draft: draft,
                        setDraft: patch
                    }),
                    create.isError && /*#__PURE__*/ _jsx("p", {
                        role: "alert",
                        children: "Couldn't save item. Your entries are retained; retry or choose a manual document reference if the linked document changed."
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        type: "submit",
                        "data-testid": Testids.rfiQuestions.save,
                        disabled: create.isPending || !draft.originalText.trim() || !draft.interpretation.trim() || Boolean(draft.artifactId && !artifacts.data?.some((a)=>a.artifactId === draft.artifactId)),
                        children: create.isPending ? 'Saving…' : 'Save item'
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        type: "button",
                        variant: "ghost",
                        disabled: create.isPending,
                        onClick: ()=>setAdding(false),
                        children: "Close entry"
                    })
                ]
            }),
            query.data && /*#__PURE__*/ _jsxs("p", {
                "data-testid": Testids.rfiQuestions.count,
                children: [
                    query.data.questions.length,
                    " items"
                ]
            }),
            query.data?.questions.length === 0 && /*#__PURE__*/ _jsx("p", {
                children: "No questions recorded yet. Extract a linked document or add an item manually. Empty results do not mean there are no requirements."
            }),
            query.data?.questions.map((q)=>/*#__PURE__*/ _jsx(QuestionRow, {
                    question: q,
                    projectId: projectId,
                    events: query.data.events.filter((e)=>e.questionId === q.id)
                }, q.id))
        ]
    });
}
